import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
} from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CircleIcon from "@mui/icons-material/Circle";
import { HIGHLIGHT_COLORS } from "../../bible/highlightStorage";
import { getBookName } from "../../bible/edition";

const processChapterVerses = (content) => {
  if (!content) return {};
  const map = {};
  const lines = content.split("\r\n").filter((l) => l.trim());
  lines.forEach((line) => {
    const match = line.trim().match(/^(\d+)\s*(.+)?$/);
    if (match) {
      map[match[1]] = match[2]?.trim() || "";
    }
  });
  return map;
};

const MyHighlightsView = ({ bibleChapters = [], onOpenChapter, edition }) => {
  const isEn = edition?.id === "en";
  const [selectedColor, setSelectedColor] = useState("all");

  const chapterMap = useMemo(() => {
    const map = {};
    bibleChapters.forEach((ch) => {
      map[ch.id] = ch;
    });
    return map;
  }, [bibleChapters]);

  const allHighlights = useMemo(() => {
    try {
      const raw =
        typeof window !== "undefined"
          ? window.localStorage.getItem("bible-verse-highlights")
          : null;
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }, []);

  const items = useMemo(() => {
    const list = [];
    Object.keys(allHighlights).forEach((chapterId) => {
      const chapterData = allHighlights[chapterId] || {};
      const chapterObj = chapterMap[chapterId];
      if (!chapterObj) return;

      const verseMap = processChapterVerses(chapterObj.content);
      const bookName = getBookName(chapterObj);

      Object.keys(chapterData).forEach((verseStr) => {
        const colorId = chapterData[verseStr];
        const verseNum = Number(verseStr);
        const text = verseMap[verseStr] || "";

        list.push({
          chapterId,
          bookName,
          chapterNumber: chapterObj.chapterNumber,
          verseNumber: verseNum,
          colorId,
          text,
        });
      });
    });

    // Sort by chapterId and verseNumber
    return list.sort((a, b) => {
      const [bA, cA] = a.chapterId.split("-").map(Number);
      const [bB, cB] = b.chapterId.split("-").map(Number);
      if (bA !== bB) return bA - bB;
      if (cA !== cB) return cA - cB;
      return a.verseNumber - b.verseNumber;
    });
  }, [allHighlights, chapterMap]);

  const filteredItems = useMemo(() => {
    if (selectedColor === "all") return items;
    return items.filter((item) => item.colorId === selectedColor);
  }, [items, selectedColor]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        minHeight: "450px",
      }}
    >
      {/* Header & Color Filters */}
      <Box sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BookmarkBorderIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {isEn ? "My Highlighted Verses" : "我的灵修划线与金句收藏"}
            </Typography>
            <Chip
              label={filteredItems.length}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 700, height: 22 }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            <Chip
              label={isEn ? "All Colors" : "全部颜色"}
              size="small"
              variant={selectedColor === "all" ? "filled" : "outlined"}
              color={selectedColor === "all" ? "primary" : "default"}
              onClick={() => setSelectedColor("all")}
              sx={{ cursor: "pointer", fontWeight: 600 }}
            />
            {HIGHLIGHT_COLORS.map((color) => {
              const label = isEn ? color.labelEn : color.labelZh;
              const isSelected = selectedColor === color.id;
              return (
                <Chip
                  key={color.id}
                  icon={
                    <CircleIcon
                      sx={{
                        color: `${color.dotColor} !important`,
                        fontSize: "12px !important",
                      }}
                    />
                  }
                  label={label}
                  size="small"
                  variant={isSelected ? "filled" : "outlined"}
                  onClick={() => setSelectedColor(color.id)}
                  sx={{
                    cursor: "pointer",
                    fontWeight: 600,
                    borderColor: isSelected ? color.dotColor : "divider",
                    bgcolor: isSelected
                      ? (theme) =>
                          theme.palette.mode === "dark"
                            ? color.bgDark
                            : color.bgLight
                      : "transparent",
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Items List */}
      {filteredItems.length === 0 ? (
        <Box
          sx={{
            py: 8,
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          <BookmarkBorderIcon sx={{ fontSize: 52, opacity: 0.35, mb: 1.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
            {isEn ? "No highlights yet" : "暂无划线经文"}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {isEn
              ? "Select verses while reading a chapter to highlight and save them here"
              : "在章节阅读页面中，点击或划选经文即可选择颜色高亮，高亮后的经文将集中在此展示"}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {filteredItems.map((item, idx) => {
            const colorDef = HIGHLIGHT_COLORS.find(
              (c) => c.id === item.colorId
            ) || HIGHLIGHT_COLORS[0];

            return (
              <Paper
                key={`${item.chapterId}-${item.verseNumber}-${idx}`}
                elevation={0}
                onClick={() =>
                  onOpenChapter(item.chapterId, item.verseNumber)
                }
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderLeft: `4px solid ${colorDef.dotColor}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: colorDef.dotColor,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    transform: "translateX(3px)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.75,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 800, color: "primary.main" }}
                    >
                      {item.bookName}{" "}
                      {isEn
                        ? `${item.chapterNumber}:${item.verseNumber}`
                        : `${item.chapterNumber}章 ${item.verseNumber}节`}
                    </Typography>
                    <Chip
                      label={isEn ? colorDef.labelEn : colorDef.labelZh}
                      size="small"
                      sx={{
                        fontSize: "0.65rem",
                        height: 18,
                        borderRadius: 1,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? colorDef.bgDark
                            : colorDef.bgLight,
                        color: "text.primary",
                      }}
                    />
                  </Box>

                  <ChevronRightIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </Box>

                {item.text ? (
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      lineHeight: 1.6,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.text}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    {isEn ? "Click to view passage" : "点击查看此节经文..."}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default MyHighlightsView;
